import logger from '../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import DatePicker from 'react-datepicker';
import { Link, useNavigate } from 'react-router-dom';
import '../style/Orders.css';
import UserDetails from './customComponents/tracker';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import GCSDocumentManager from './customComponents/GCSDocumentManager';
import "../style/GCSDocumentManager.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation } from 'react-router-dom';
import { dateOfBirth, formatUTCForDisplay, convertLocalToUTC, formatTo12Hour, getUserId, numberedList, formatDateOfBirth, capitalize, formatTimePlusFiveHours, convertUTCToLocal, formatAppointmentDate } from '../utils/utils';
import { CalendarClock, Home, UserCheck, DollarSign, FlaskConical, Aperture } from 'lucide-react';
import PageTopBar from './customComponents/PageTopBar';
import useImagingCatalog from '../hooks/useImagingCatalog';
import { modalities, regionBodyPartsMap, anatomicalMap } from '../utils/imagingData';

// Clinic hours — appointments only run 9am-6pm, so the reschedule picker
// shouldn't offer anything outside that window (react-datepicker's
// minTime/maxTime only look at the hours/minutes, not the date, so any day
// works as the carrier).
const APPOINTMENT_MIN_TIME = new Date();
APPOINTMENT_MIN_TIME.setHours(9, 0, 0, 0);
const APPOINTMENT_MAX_TIME = new Date();
APPOINTMENT_MAX_TIME.setHours(18, 0, 0, 0);

// Reschedule Modal
function RescheduleModal({ appointment, onClose, onReschedule, bookedSlots, newDate, onDateChange }) {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const handleReschedule = () => {
    if (!newDate) {
      toast.error('Please select a new date and time');
      return;
    }
    onReschedule(appointment, newDate);
  };

  // Keyed by 'YYYY-MM-DD' -> { clinicClosed, blockedModalities, allLabsBlocked,
  // blockedSpecimens } — mirrors Scheduling.js's RescheduleModal so rescheduling
  // can't be dragged onto a clinic-closed or equipment/staff-blocked day.
  const [unavailableDates, setUnavailableDates] = useState({});

  const fetchUnavailableDates = async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [closureRes, modalityRes, labRes] = await Promise.all([
        fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/modalityAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/labAvailability?startDate=${startDate}&endDate=${endDate}`),
      ]);
      const closures = closureRes.ok ? await closureRes.json() : [];
      const modalityData = modalityRes.ok ? await modalityRes.json() : {};
      const labData = labRes.ok ? await labRes.json() : {};

      setUnavailableDates(prev => {
        const merged = { ...prev };
        closures.forEach(c => {
          const d = c.blocked_date.split('T')[0];
          merged[d] = { ...(merged[d] || {}), clinicClosed: true };
        });
        Object.keys(modalityData).forEach(d => {
          merged[d] = { ...(merged[d] || {}), blockedModalities: modalityData[d].blockedModalities };
        });
        Object.keys(labData).forEach(d => {
          merged[d] = {
            ...(merged[d] || {}),
            allLabsBlocked: labData[d].allBlocked,
            blockedSpecimens: labData[d].blockedSpecimens,
          };
        });
        return merged;
      });
    } catch (error) {
      logger.error('Error fetching unavailable dates', error);
    }
  };

  useEffect(() => {
    fetchUnavailableDates(newDate || new Date());
  }, []);

  const getDateUnavailableReason = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const info = unavailableDates[dateStr];
    if (!info) return null;
    if (info.clinicClosed) return 'Clinic closed';
    if (appointment.visit_type === 'Radiology' && info.blockedModalities?.includes(appointment.exam)) {
      return `No rad tech available to run ${appointment.exam}`;
    }
    if (appointment.visit_type === 'Lab') {
      if (info.allLabsBlocked) return 'No lab-capable staff available';
      if (info.blockedSpecimens?.includes(appointment.exam)) return `No staff available to run ${appointment.exam} labs`;
    }
    return null;
  };
  const isDateUnavailable = (date) => getDateUnavailableReason(date) !== null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reschedule Appointment</h3>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        <div className="modal-body">
          <div className="current-appointment-info">
            <p><strong>{appointment.first_name} {appointment.last_name}</strong></p>
            <p>{appointment.visit_type} - {appointment.exam || 'N/A'}</p>
            {/* <p>Current: {formatDateOfBirth(convertUTCToCentral(appointment.scheduled_time))} at {formatTimePlusFiveHours(appointment.scheduled_time)}</p> */}
            <p>Current: {formatUTCForDisplay(appointment.appointment_start) || formatUTCForDisplay(appointment.scheduled_time)}</p>
          </div>
          <div className="form-field">
            <label>Select New Date & Time</label>
            <span className="schedule-date-legend">Greyed-out dates are unavailable — clinic closed or no staff/equipment to perform this exam.</span>
            <DatePicker
              selected={newDate}
              onChange={onDateChange}
              onMonthChange={fetchUnavailableDates}
              showTimeSelect
              minTime={APPOINTMENT_MIN_TIME}
              maxTime={APPOINTMENT_MAX_TIME}
              timeIntervals={appointment.visit_type === "Radiology" ? 30 : 10}
              dateFormat="MMMM d, yyyy h:mm aa"
              className="modern-datepicker"
              minDate={new Date()}
              excludeTimes={bookedSlots}
              filterDate={(date) => !isDateUnavailable(date)}
              dayClassName={(date) => getDateUnavailableReason(date) ? 'schedule-day-unavailable' : undefined}
              renderDayContents={(dayOfMonth, date) => {
                const reason = date ? getDateUnavailableReason(date) : null;
                return <span title={reason || undefined}>{dayOfMonth}</span>;
              }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Cancel</button>
          <button onClick={handleReschedule} className="primary-btn">Confirm Reschedule</button>
        </div>
      </div>
    </div>
  );
}

const Orders = ({ patientId, userRole }) => {
  const { currentPatient } = useContext(GlobalStateContext);
  const location = useLocation();
  const [scheduleTimes, setScheduleTimes] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.orderType || 'Radiology');
  const [isEditing, setIsEditing] = useState(false);
  const [radiologyOrders, setRadiologyOrders] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [scannedFile, setScannedFile] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedTimeId, setSelectedTimeId] = useState(null);
  const [options, setOptions] = useState([]);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const {currentUser} = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const navigate = useNavigate();
  const [timeModal, setTimeModal] = useState(false);
  const currentDate = new Date();
  const [editRegion, setEditRegion] = useState("");
  const [editBodyPart, setEditBodyPart] = useState("");
  const [editCptCodesFiltered, setEditCptCodesFiltered] = useState([]);
  const [editSelectedExamId, setEditSelectedExamId] = useState("");
  const { mergedAnatomicalMap, mergedRegionBodyPartsMap } = useImagingCatalog(anatomicalMap, regionBodyPartsMap);


  const handleReschedule = async (appointment, newDate) => {
    try {
      logger.log(appointment, newDate)
      const response = await fetch(`${BASE_URL}/scheduling/reschedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: appointment.schedule_id,
          appointment_start: convertLocalToUTC(newDate),
          exam_type: appointment.exam,
          visit_type: appointment.visit_type,
          user,
          type: currentUser.type
        })
      });

      if (response.ok) {
        const data = await response.json();
        logger.log(data)
        toast.success('Appointment rescheduled successfully!');
        setRescheduleAppt(null);
        setRescheduleDate(null);
        // Refresh the orders to show updated appointment time
        if (currentPatient?.ris_patients_id) {
          const ordersResponse = await fetch(
            `${BASE_URL}/orders/${activeTab === 'Radiology' ? 'radOrders' : 'labOrders'}?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
          );
          if (ordersResponse.ok) {
            const ordersData = await ordersResponse.json();
            if (activeTab === 'Radiology') {
              setRadiologyOrders(Array.isArray(ordersData) ? ordersData : []);
            } else {
              setLabOrders(Array.isArray(ordersData) ? ordersData : []);
            }
          }
        }
      } else {
        toast.error('Failed to reschedule appointment');
      }
    } catch (error) {
      logger.error('Error rescheduling appointment:', error);
      toast.error('Error rescheduling appointment');
    }
  };

  const handleOptionsChange = (e) => {
    const value = e.target.value;
    const updatedOptions = options.includes(value)
      ? options.filter((opt) => opt !== value)
      : [...options, value];
    
    setOptions(updatedOptions);

    const formattedOptions = updatedOptions.join('||');

    setCurrentOrder((prevOrder) => ({
      ...prevOrder,
      additional_options: formattedOptions,
    }));
  };

  const [sendingToWorklist, setSendingToWorklist] = useState(false);

  const handleSendToAdvaPACS = async (orderId) => {
    setSendingToWorklist(true);
    try {
      const response = await fetch(`${BASE_URL}/worklist/send/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success) {
        toast.success(`Order ${orderId} sent to AdvaPACS (ACK: ${result.ackCode})`);
      } else {
        toast.error(`Failed to send order ${orderId}: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      logger.error('Error sending to AdvaPACS:', err);
      toast.error('Error sending order to AdvaPACS');
    } finally {
      setSendingToWorklist(false);
    }
  };

  const handleEdit = async () => {
    handleEditToggle();
    await getScheduleTimes();
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const getScheduleTimes = async () => {
    try {
        const response = await fetch(
          `${BASE_URL}/orders/getScheduleTimes?visit_type=${activeTab}?user=${user}&type=${currentUser.type}`
        );
        if (response.ok) {
          const data = await response.json();
          setScheduleTimes((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(data)) {
            return data;
          }
          return prev;
        })
      } 
    } catch (error) {
        logger.error('Error fetching orders:', error);
      }
  }

  const handleInputChange = (e, orderType) => {
    const { name, value } = e.target;
    setCurrentOrder((prevOrder) => ({
      ...prevOrder,
      [name]: value,
    }));
  };

  const handleSave = (orderType) => {
    if (!currentOrder) return;

    // Update the respective orders array
    if (orderType === 'Radiology') {
      setRadiologyOrders((prev) =>
        prev.map((order) =>
          order.id === currentOrder.id ? currentOrder : order
        )
      );
      radiologyOrderPut();
    } else {
      setLabOrders((prev) =>
        prev.map((order) =>
          order.id === currentOrder.id ? currentOrder : order
        )
      );
      labOrderPut();
    }

    setIsEditing(false);
  };

  const radiologyOrderPut = async () => {
    try {
        // logger.log('Radiology Order Put info: ', currentOrder, selectedTimeId)
        const response = await fetch(
          `${BASE_URL}/orders/updateRadOrder`, {
            method: 'PUT',
             headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({currentOrder, selectedTimeId, options, user, type: currentUser.type}),
          }
        );
        if (response.ok) {
          const data = await response.json();
          // logger.log('Radiology order:', JSON.stringify(data));
          // setScheduleTimes(data);
        }
      } catch (error) {
        logger.error('Error updating rad order:', error);
      }
  }

  const labOrderPut = async () => {
    try {
        const response = await fetch(
          `${BASE_URL}/orders/updateLabOrder`, {
            method: 'PUT',
             headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({currentOrder, selectedTimeId, options, user, type: currentUser.type}),
          }
        );
        if (response.ok) {
          const data = await response.json();
          // logger.log('Lab order:', JSON.stringify(data));
          // setScheduleTimes(data);
        }
      } catch (error) {
        logger.error('Error updating lab order:', error);
      }
  }

  const handleOrderSelect = (e) => {
    const orderId = e.target.value;
    const orders = activeTab === 'Radiology' ? radiologyOrders : labOrders;
    const selectedOrder = orders.find((order) => order.id === Number(orderId));
    logger.log(selectedOrder)
    setCurrentOrder(selectedOrder || null);
  };

  const handleConfirm = (updatedData) => {
    if (currentOrder && Number(updatedData.id) === currentOrder.schedule_id) {
      setCurrentOrder({ ...currentOrder, appointment_start: updatedData.updatedTime });
    }

    if (activeTab === 'Radiology') {
      setRadiologyOrders((order) => 
        order.map((o) => 
          o.schedule_id === Number(updatedData.id)
            ? { ...o, appointment_start: updatedData.updatedTime }
            : o
        )
      );
    } else {
      setLabOrders((order) =>
        order.map((o) =>
          o.schedule_id === Number(updatedData.id)
            ? { ...o, appointment_start: updatedData.updatedTime }
            : o
        )
      );
    }
    setTimeModal(false);
  };

  const handleCancel = () => {
    // logger.log('Modal canceled');
    setTimeModal(false);
  };

  const fetchBookedSlots = async (visit_type, exam, selectedDate) => {
    try {
      const dateStr = selectedDate 
        ? selectedDate.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];
      
      const response = await fetch(
        `${BASE_URL}/scheduling/bookedSlotsWorklist?visit_type=${visit_type}&exam=${exam}&date=${dateStr}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const excludedTimes = [];
          
          data.forEach((slot) => {
            // Use helper function to convert UTC to local
            const startTime = convertUTCToLocal(slot.appointment_start);
            const endTime = convertUTCToLocal(slot.appointment_end);
            
            let currentTime = new Date(startTime);
            while (currentTime < endTime) {
              excludedTimes.push(new Date(currentTime));
              const intervalMinutes = visit_type === "Radiology" ? 30 : 10;
              currentTime = new Date(currentTime.getTime() + intervalMinutes * 60000);
            }
            
            excludedTimes.push(new Date(endTime));
          });
          
          setBookedSlots(excludedTimes);
        } else {
          setBookedSlots([]);
        }
      }
    } catch (error) {
      logger.error("Error fetching booked slots", error);
      toast.error("Error fetching available slots");
    }
  };

  // Initial load - fetch booked slots when reschedule modal opens
  useEffect(() => {
    if (rescheduleAppt && rescheduleAppt.visit_type && rescheduleAppt.visit_type !== 'Lab' && rescheduleAppt.exam) {
      fetchBookedSlots(rescheduleAppt.visit_type, rescheduleAppt.exam, currentDate);
    }
  }, [rescheduleAppt]);

  // Refetch when user selects a date in the reschedule modal
  useEffect(() => {
    if (rescheduleDate && rescheduleAppt && rescheduleAppt.visit_type !== 'Lab' && rescheduleAppt.exam) {
      fetchBookedSlots(rescheduleAppt.visit_type, rescheduleAppt.exam, rescheduleDate);
    }
  }, [rescheduleDate?.toDateString()]);

  useEffect(() => {
  if (!currentPatient?.ris_patients_id) return;
  setCurrentOrder(null);
  const fetchRadOrders = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/orders/radOrders?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        setRadiologyOrders((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(data)) {
            return Array.isArray(data) ? data : [];
          }
          return prev;
        });
        // setRadiologyOrders(data[0])
        if (activeTab === 'Radiology' && data.length > 0) {
          const targetId = location.state?.orderId;
          const match = targetId ? data.find(o => o.id === Number(targetId)) : null;
          setCurrentOrder(match || data[0]);
        }
      }
    } catch (error) {
      logger.error('Error fetching Radiology orders:', error);
    }
  };

  const fetchLabOrders = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/orders/labOrders?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`
      );
      if (response.ok) {
        const data = await response.json();
        logger.log(data)
        // logger.log(data)
        setLabOrders((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(data)) {
            return Array.isArray(data) ? data : [];
          }
          return prev;
        });
        if (activeTab === 'Lab' && data.length > 0) {
          const targetId = location.state?.orderId;
          const match = targetId ? data.find(o => o.id === Number(targetId)) : null;
          setCurrentOrder(match || data[0]);
        }
      }
    } catch (error) {
      logger.error('Error fetching Lab orders:', error);
    }
  };

    fetchRadOrders();
    fetchLabOrders();
  }, [currentPatient?.ris_patients_id, activeTab]);

useEffect(() => {
  if (currentOrder && currentOrder.additional_options) {
    setOptions(
      Array.isArray(currentOrder.additional_options)
        ? currentOrder.additional_options
        : currentOrder.additional_options.split('||')
    );
  } else {
    setOptions([]);
  }
}, [currentOrder, isEditing, activeTab]);

useEffect(() => {
  if (isEditing && currentOrder) {
    setEditRegion(currentOrder.region || "");
    setEditBodyPart(currentOrder.body_part || "");
    setEditSelectedExamId("");
  }
}, [isEditing]);

useEffect(() => {
  if (!editRegion || !currentOrder?.modality) {
    setEditCptCodesFiltered([]);
    return;
  }
  const regionEntries = mergedAnatomicalMap[editRegion] || [];
  setEditCptCodesFiltered(regionEntries.filter(item => item.modality === currentOrder.modality));
  setEditSelectedExamId("");
}, [editRegion, currentOrder?.modality, mergedAnatomicalMap]);


  const renderOrderDetails = (order, orderType) => {
    if (!order) {
      return <h3>Select A Patient | No order selected/available</h3>;
    }

    return (
      <div className="ris-orders-details">
        <div className="ris-orders-form-group">
          <label>Scheduled Time:</label>
          {order.appointment_start ? (
            isEditing ? (
              <>
                <p>{formatUTCForDisplay(order.appointment_start, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <button
                  onClick={() => setRescheduleAppt(order)}
                  className="icon-btn"
                  title="Reschedule"
                >
                  <CalendarClock />
                </button>
              </>
            ) : (
              <span>
                {formatUTCForDisplay(order.appointment_start, { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )
          ) : (
            <div className="worklist-pending-row">
              <span className="worklist-pending-label">Order in Worklist</span>
              <button
                className="worklist-redirect-btn"
                onClick={() => navigate('/orderWorklist', { state: { orderId: order.id, orderType: activeTab } })}
              >
                Go to Worklist to Schedule
              </button>
            </div>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Patient Name:</label>
          {isEditing ? (
            <input
              type="text"
              name="patient_name"
              value={order.patient_name || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>{order.patient_name || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Ordering Clinician:</label>
          {isEditing ? (
            <input
              type="text"
              name="ordering_clinician"
              value={order.ordering_clinician || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>{order.ordering_clinician || 'N/A'}</span>
          )}
        </div>
        {orderType === 'Radiology' && (
          <div className="ris-orders-form-group">
            <label>Modality:</label>
            {isEditing ? (
              <select
                name="modality"
                value={order.modality || ''}
                onChange={(e) => {
                  handleInputChange(e, orderType);
                  setEditCptCodesFiltered([]);
                  setEditSelectedExamId("");
                }}
              >
                <option value="">Select Modality</option>
                {modalities.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            ) : (
              <span>{order.modality || 'N/A'}</span>
            )}
          </div>
        )}
        {orderType === 'Radiology' && (
          <div className="ris-orders-form-group">
            <label>Region:</label>
            {isEditing ? (
              <select
                name="region"
                value={editRegion}
                onChange={(e) => {
                  const newRegion = e.target.value;
                  setEditRegion(newRegion);
                  setEditBodyPart("");
                  setEditSelectedExamId("");
                  handleInputChange({ target: { name: 'region', value: newRegion } }, orderType);
                  handleInputChange({ target: { name: 'body_part', value: '' } }, orderType);
                }}
              >
                <option value="">Select Region</option>
                {Object.keys(mergedAnatomicalMap).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            ) : (
              <span>{order.region || 'N/A'}</span>
            )}
          </div>
        )}
        {orderType === 'Radiology' && (
          <div className="ris-orders-form-group">
            <label>Body Part:</label>
            {isEditing ? (
              <select
                name="body_part"
                value={editBodyPart}
                onChange={(e) => {
                  const newBodyPart = e.target.value;
                  setEditBodyPart(newBodyPart);
                  handleInputChange({ target: { name: 'body_part', value: newBodyPart } }, orderType);
                }}
                disabled={!editRegion}
              >
                <option value="">{editRegion ? 'Select Body Part' : 'Select Region first'}</option>
                {(mergedRegionBodyPartsMap[editRegion] || []).map(bp => (
                  <option key={bp} value={bp}>{bp}</option>
                ))}
              </select>
            ) : (
              <span>{order.body_part || 'N/A'}</span>
            )}
          </div>
        )}
        <div className="ris-orders-form-group">
          <label>Exam Ordered:</label>
          {isEditing ? (
            orderType === 'Radiology' ? (
              <select
                value={editSelectedExamId}
                onChange={(e) => {
                  const val = e.target.value;
                  const id = isNaN(Number(val)) ? val : Number(val);
                  const selected = editCptCodesFiltered.find(c => String(c.id) === String(val));
                  if (selected) {
                    setEditSelectedExamId(val);
                    handleInputChange({ target: { name: 'exam_ordered', value: selected.name } }, orderType);
                    handleInputChange({ target: { name: 'cpt_code', value: selected.code } }, orderType);
                  }
                }}
                disabled={!editCptCodesFiltered.length}
              >
                <option value="">{editCptCodesFiltered.length ? 'Select Exam' : 'Select Region & Modality first'}</option>
                {editCptCodesFiltered.map(({ id, code, name }) => (
                  <option key={id} value={id}>{code} – {name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="exam_ordered"
                value={order.exam_ordered || ''}
                onChange={(e) => handleInputChange(e, orderType)}
              />
            )
          ) : (
            <span>{order.exam_ordered || 'N/A'}</span>
          )}
        </div>
        {orderType === 'Lab' && (
          <div className="ris-orders-form-group">
            <label>Specimen Type:</label>
            {isEditing ? (
              <input
                type="text"
                name="specimen_type"
                value={order.specimen_type || ''}
                onChange={(e) => handleInputChange(e, orderType)}
              />
            ) : (
              <span>{order.specimen_type || 'N/A'}</span>
            )}
          </div>
        )}
        <div className="ris-orders-form-group">
          <label>Status:</label>
          {isEditing ? (
            <select
              name="status"
              value={order.status || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            >
              <option value="scheduled">Scheduled</option>
              <option value="arrived">Arrived</option>
              <option value="scanned">Scanned</option>
              <option value="transmitting">Transmitting</option>
              <option value="received">Received</option>
              <option value="complete">Complete</option>
            </select>
          ) : (
            <span>{capitalize(order.status) || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Priority:</label>
          {isEditing ? (
            <select
              name="priority"
              value={order.priority || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            >
              <option value="Routine">Routine</option>
              <option value="STAT">STAT</option>
              <option value="Stroke">Stroke</option>
              <option value="Trauma">Trauma</option>
              <option value="Next Day">Next Day</option>
              <option value="2 Day">2 Day</option>
            </select>
          ) : (
            <span>{order.priority || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>CPT Code:</label>
          {isEditing && orderType === 'Radiology' ? (
            <span>{order.cpt_code || 'Auto-populated from exam selection'}</span>
          ) : isEditing ? (
            <input
              type="text"
              name="cpt_code"
              value={order.cpt_code || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>{order.cpt_code || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Clinical History:</label>
          {isEditing ? (
            <textarea
              name="clinical_history"
              value={order.clinical_history || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>{order.clinical_history || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Notes:</label>
          {isEditing ? (
            <textarea
              name="notes"
              value={order.notes || ''}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>{order.notes || 'N/A'}</span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Additional Options:</label>
          {isEditing ? (
            <fieldset className="options">
          <legend>Additional Options</legend>
          {["IV Gadolinium","IV Contrast", "Oral Contrast", "Enteric Contrast", "Sedation", "Fluids"].map((opt) => (
            <label key={opt}>
              <input
                type="checkbox"
                value={opt}
                checked={options.includes(opt)}
                onChange={handleOptionsChange}
              />
              {opt}
            </label>
          ))}
        </fieldset>
          ) : (
          <span>{numberedList(order.additional_options) || "None"}</span>
          )} 
        </div>
        <div className="ris-orders-form-group">
          <label>Order Timestamp:</label>
          <span>
            {formatUTCForDisplay(order.created_at)}
          </span>
        </div>
        <div className="ris-orders-form-group">
          <label>Date of Service:</label>
          {isEditing ? (
            <input
              type="date"
              name={orderType === 'Radiology' ? 'performed_date' : 'specimen_collected_date'}
              value={orderType === 'Radiology' ? ((order.performed_date || '').split('T')[0]) : ((order.specimen_collected_date || '').split('T')[0])}
              onChange={(e) => handleInputChange(e, orderType)}
            />
          ) : (
            <span>
              {orderType === 'Radiology'
                ? (order.performed_date ? dateOfBirth(order.performed_date) : 'N/A')
                : (order.specimen_collected_date ? dateOfBirth(order.specimen_collected_date) : 'N/A')
              }
            </span>
          )}
        </div>
        <div className="ris-orders-form-group">
          <label>Scan Paper Order:</label>
          <GCSDocumentManager orderId={order.id} orderType={orderType} />
          {scannedFile && <span>File uploaded: {scannedFile}</span>}
        </div>
        <div className="ris-orders-buttons">
          {isEditing ? (
            <>
              <button onClick={() => handleSave(orderType)}>Save</button>
              <button onClick={handleEditToggle}>Cancel</button>
            </>
          ) : (
            <button onClick={() => handleEdit()}>Edit</button>
          )}
          {orderType === 'Radiology' && !isEditing && (
            <button
              onClick={() => handleSendToAdvaPACS(order.id)}
              disabled={sendingToWorklist}
            >
              {sendingToWorklist ? 'Sending...' : 'Send Order to AdvaPACS'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ris-orders">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <PageTopBar links={[
        { label: 'Check-In', to: '/checkIn', icon: UserCheck },
        { label: 'Home', to: '/home', icon: Home },
        { label: 'Billing', to: '/billing', icon: DollarSign },
        { label: 'Payments', to: '/hgiPayments', icon: DollarSign },
        { label: 'Labs', to: '/labs', icon: FlaskConical },
        { label: 'Radiology', to: '/radiology', icon: Aperture },
      ]} />
      <div className="ris-orders-content">
        <div className="ris-orders-page-header">
          <h1 className="ris-orders-page-title">Orders</h1>
        </div>
      <div className="ris-orders-tabs">
        <button
          className={activeTab === 'Radiology' ? 'active' : ''}
          onClick={() => setActiveTab('Radiology')}
        >
          Diagnostic Radiology
        </button>
        <button
          className={activeTab === 'Lab' ? 'active' : ''}
          onClick={() => setActiveTab('Lab')}
        >
          Lab Test
        </button>
        <select
          className="order-selector-box"
          value={currentOrder?.id || ''}
          onChange={handleOrderSelect}
        >
          <option value="">Select an order</option>
          {(activeTab === 'Radiology' ? radiologyOrders : labOrders).map((order) => (
            <option key={order.id} value={order.id} className="order-selector">
              {activeTab} ID: {order.id} | {order.exam_ordered} {order.appointment_start ? `on ${formatUTCForDisplay(order.appointment_start)}` : '— In Worklist'}
            </option>
          ))}
        </select>
      </div>
      {currentOrder ? (
        renderOrderDetails(currentOrder, activeTab)
      ) : (
        <h3>No order selected</h3>
      )}
      {rescheduleAppt && (
        <RescheduleModal
          appointment={rescheduleAppt}
          onClose={() => {
            setRescheduleAppt(null);
            setRescheduleDate(null);
            setBookedSlots([]);
          }}
          onReschedule={handleReschedule}
          bookedSlots={bookedSlots}
          newDate={rescheduleDate}
          onDateChange={setRescheduleDate}
        />
      )}
      </div>
    </div>
  );
};

export default Orders;